// Source-entry builders for link/inline runtime compilation inputs.
// Example signature: `link:${href}\n${rawSource}`.
import type { CachedSourceEntry, BuildSourceEntry } from './types'
import type { LuisSourceElement } from '../context'
import { ensureCompiledStyleTag, resolveLuisSourceHref } from '../dom'
import { resolveManifestModeResolver } from './manifest'
import { buildInlineSourceEntry } from './source-entry-inline'
import { buildLinkSourceEntry } from './source-entry-link'

// Build source descriptors with stable ids and cached source text reuse.
/** @internal Build source descriptors with stable ids and cached source text reuse. */
export async function buildSourceEntries(
  sources: LuisSourceElement[],
  previousCache: CachedSourceEntry[],
): Promise<BuildSourceEntry[]> {
  const entries: BuildSourceEntry[] = []
  const previousById = new Map(previousCache.map((entry) => [entry._sourceId, entry]))

  // Resolve each source into a compilable entry in DOM order.
  for (const source of sources) {
    const styleTag = ensureCompiledStyleTag(source)
    const sourceId = source.dataset.luisStyleId ?? styleTag.id
    const previous = previousById.get(sourceId)
    const manifestResolution = await resolveManifestModeResolver(source)

    if (source instanceof HTMLLinkElement) {
      const href = resolveLuisSourceHref(source)
      entries.push(await buildLinkSourceEntry(source, styleTag, sourceId, href, previous, manifestResolution))
    } else {
      // Continue with inline source entry build.
      entries.push(await buildInlineSourceEntry(source, styleTag, sourceId, manifestResolution))
    }
  }

  // Return source entries in source order.
  return entries
}
